import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Autocomplete,
  Box,
  Typography,
  Divider,
  Collapse,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  MaleRounded as MaleIcon,
  FemaleRounded as FemaleIcon,
} from '@mui/icons-material';
import {
  AddRounded as AddIcon,
  ExpandMoreRounded as ExpandMoreIcon,
  ExpandLessRounded as ExpandLessIcon,
  HomeRounded as HomeIcon,
} from '@mui/icons-material';
import { CREATE_PERSON, UPDATE_PERSON, CREATE_HOUSEHOLD } from '../apollo/mutations';
import { GET_PERSONS, GET_HOUSEHOLDS } from '../apollo/queries';
import CloudinaryUpload from './CloudinaryUpload';

const schema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  middleName: yup.string().nullable(),
  preferredName: yup.string().nullable(),
  email: yup.string().email('Invalid email format').nullable(),
  phone: yup.string().nullable(),
  birthday: yup.string().nullable(),
  deceasedDate: yup.string().nullable(),
  gender: yup.string().nullable().oneOf(['male', 'female', null]),
  householdId: yup.string().nullable(),
  profilePictureUrl: yup.string().nullable(), // Now stores public ID, not URL
  notes: yup.string().nullable(),
});

const defaultValues = {
  firstName: '',
  lastName: '',
  middleName: '',
  preferredName: '',
  email: '',
  phone: '',
  birthday: '',
  deceasedDate: '',
  gender: null,
  householdId: null,
  profilePictureUrl: '',
  notes: '',
};

function PersonFormDialog({ open, onClose, person = null }) {
  const isEditing = Boolean(person);
  const [showNewHousehold, setShowNewHousehold] = useState(false);
  const [newHouseholdData, setNewHouseholdData] = useState({
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  });

  const { data: householdsData } = useQuery(GET_HOUSEHOLDS);
  const households = householdsData?.households || [];

  const { control, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: person ? { ...defaultValues, ...person } : defaultValues,
  });

  const selectedHouseholdId = watch('householdId');
  const selectedHousehold = households.find((h) => h.id === selectedHouseholdId);

  React.useEffect(() => {
    if (open) {
      reset(person ? { ...defaultValues, ...person } : defaultValues);
      setShowNewHousehold(false);
      setNewHouseholdData({
        name: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
      });
    }
  }, [open, person, reset]);

  const [createPerson, { loading: creating, error: createError }] = useMutation(CREATE_PERSON, {
    refetchQueries: [{ query: GET_PERSONS }, { query: GET_HOUSEHOLDS }],
    onCompleted: () => {
      onClose();
      reset(defaultValues);
    },
  });

  const [updatePerson, { loading: updating, error: updateError }] = useMutation(UPDATE_PERSON, {
    refetchQueries: [{ query: GET_PERSONS }, { query: GET_HOUSEHOLDS }],
    onCompleted: () => {
      onClose();
    },
  });

  const [createHousehold, { loading: creatingHousehold }] = useMutation(CREATE_HOUSEHOLD, {
    refetchQueries: [{ query: GET_HOUSEHOLDS }],
  });

  const loading = creating || updating || creatingHousehold;
  const error = createError || updateError;

  const onSubmit = async (data) => {
    // Only include fields that are part of PersonInput
    const validFields = [
      'firstName', 'middleName', 'lastName', 'preferredName',
      'email', 'phone', 'birthday', 'deceasedDate', 'gender',
      'householdId', 'profilePictureUrl', 'notes'
    ];

    // Clean up: only include valid fields and convert empty strings to null
    const input = {};
    validFields.forEach((key) => {
      const value = data[key];
      input[key] = value === '' ? null : value;
    });

    // If creating a new household, create it first
    if (showNewHousehold && newHouseholdData.name) {
      try {
        const householdInput = {};
        Object.entries(newHouseholdData).forEach(([key, value]) => {
          householdInput[key] = value === '' ? null : value;
        });
        const result = await createHousehold({ variables: { input: householdInput } });
        input.householdId = result.data.createHousehold.id;
      } catch (err) {
        console.error('Failed to create household:', err);
        return;
      }
    }

    if (isEditing) {
      updatePerson({ variables: { id: person.id, input } });
    } else {
      createPerson({ variables: { input } });
    }
  };

  const formatHouseholdAddress = (h) => {
    const parts = [h.addressLine1, h.city, h.state].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'No address';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditing ? 'Edit Person' : 'Add Family Member'}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error.message}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="First Name *" fullWidth error={!!errors.firstName}
                    helperText={errors.firstName?.message} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="middleName"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Middle Name" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Last Name *" fullWidth error={!!errors.lastName}
                    helperText={errors.lastName?.message} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="preferredName"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Nickname / Preferred Name" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Profile Photo
              </Typography>
              <Controller
                name="profilePictureUrl"
                control={control}
                render={({ field }) => (
                  <CloudinaryUpload
                    value={field.value}
                    onChange={field.onChange}
                    personName={`${watch('firstName')} ${watch('lastName')}`}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="email" control={control}
                render={({ field }) => (
                  <TextField {...field} label="Email" fullWidth type="email"
                    error={!!errors.email} helperText={errors.email?.message} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="phone" control={control}
                render={({ field }) => (
                  <TextField {...field} label="Phone" fullWidth />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="birthday" control={control}
                render={({ field }) => (
                  <TextField {...field} label="Birthday" fullWidth type="date"
                    InputLabelProps={{ shrink: true }} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="deceasedDate" control={control}
                render={({ field }) => (
                  <TextField {...field} label="Deceased Date" fullWidth type="date"
                    InputLabelProps={{ shrink: true }} />
                )} />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Gender
                    </Typography>
                    <ToggleButtonGroup
                      value={field.value}
                      exclusive
                      onChange={(_, value) => field.onChange(value)}
                      size="small"
                    >
                      <ToggleButton value="male" sx={{ px: 3 }}>
                        <MaleIcon sx={{ mr: 0.5 }} /> Male
                      </ToggleButton>
                      <ToggleButton value="female" sx={{ px: 3 }}>
                        <FemaleIcon sx={{ mr: 0.5 }} /> Female
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                )}
              />
            </Grid>

            {/* Household Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <HomeIcon fontSize="small" /> Household
              </Typography>
            </Grid>

            {!showNewHousehold ? (
              <>
                <Grid item xs={12}>
                  <Autocomplete
                    options={households}
                    getOptionLabel={(h) => h.name}
                    value={selectedHousehold || null}
                    onChange={(_, newValue) => {
                      setValue('householdId', newValue?.id || null);
                    }}
                    renderOption={(props, h) => (
                      <Box component="li" {...props} key={h.id}>
                        <Box>
                          <Typography variant="body1">{h.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatHouseholdAddress(h)}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Household" placeholder="Search households..." />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={() => setShowNewHousehold(true)}
                  >
                    Create New Household
                  </Button>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight={500}>New Household</Typography>
                    <Button size="small" onClick={() => setShowNewHousehold(false)}>
                      Select Existing Instead
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Household Name *"
                    fullWidth
                    value={newHouseholdData.name}
                    onChange={(e) => setNewHouseholdData({ ...newHouseholdData, name: e.target.value })}
                    placeholder="e.g., The Smith Family"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Address Line 1"
                    fullWidth
                    value={newHouseholdData.addressLine1}
                    onChange={(e) => setNewHouseholdData({ ...newHouseholdData, addressLine1: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Address Line 2"
                    fullWidth
                    value={newHouseholdData.addressLine2}
                    onChange={(e) => setNewHouseholdData({ ...newHouseholdData, addressLine2: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="City"
                    fullWidth
                    value={newHouseholdData.city}
                    onChange={(e) => setNewHouseholdData({ ...newHouseholdData, city: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    label="State"
                    fullWidth
                    value={newHouseholdData.state}
                    onChange={(e) => setNewHouseholdData({ ...newHouseholdData, state: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    label="Postal Code"
                    fullWidth
                    value={newHouseholdData.postalCode}
                    onChange={(e) => setNewHouseholdData({ ...newHouseholdData, postalCode: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Country"
                    fullWidth
                    value={newHouseholdData.country}
                    onChange={(e) => setNewHouseholdData({ ...newHouseholdData, country: e.target.value })}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12}>
              <Controller name="notes" control={control}
                render={({ field }) => (
                  <TextField {...field} label="Notes" fullWidth multiline rows={3} />
                )} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : isEditing ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default PersonFormDialog;

